import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Refresh, ContentCopy, Share } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import { Popup } from '@/components/common/Popup';
import { generate } from '@/tmp/TargetService';
import { isShareAvailable, share } from '@/utilities/shareUtils';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

import { DateTimeRangeSelect } from '@/components/common/date/DateTimeRangeSelect';
import './SharePopup.css';

export function Body({ targetId }: { targetId: number }) {
  const { t, i18n } = useTranslation();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [embedded, setEmbedded] = useState<boolean>(false);
  const [link, setLink] = useState<string | undefined>(undefined);
  const [dates, setDates] = useState<{ from: Date; to: Date }>({
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
  });

  const ref = useRef(null);

  // a felirat athuzva marad :(
  // ezert bele kell focus-alni a textareaba
  // valami MUI bug
  // z-index nem segit
  function focusTextarea() {
    if (ref.current) {
      const e1 = ref.current as HTMLElement;
      const e2 = e1.firstChild as HTMLElement;
      e2.focus();
    }
  }

  async function createLink() {
    if (!isReadyToGenerate()) {
      return;
    }
    try {
      const tokenReceived = await generate(targetId, dates.from.getTime() * 1000, dates.to.getTime() * 1000, undefined);
      setToken(tokenReceived);
      setLink(formatLink(tokenReceived, embedded));
      setTimeout(focusTextarea, 100);
    } catch (error) {
      rethrowIfNotClientException(error);
    }
  }

  function toggleLink() {
    const toogledEmbedded = !embedded;
    setEmbedded(toogledEmbedded);
    if (token) {
      setLink(formatLink(token, toogledEmbedded));
    }
  }

  function formatLink(t: string, embedded: boolean) {
    const lang = i18n.language;
    const url = new URL(window.location.href);
    if (url.hostname !== 'localhost') {
      url.hostname = `${import.meta.env.VITE_SHARE_PREFIX}.${url.hostname}`;
    }
    // ha van a vegen # akkor letoroljuk
    url.hash = '';
    // ha van search param akkor toroljuk
    url.search = '';
    url.searchParams.set('t', t);
    url.searchParams.set('l', lang);

    url.pathname = '/tracking';

    if (embedded) {
      return `<iframe src="${url.toString()}" width="800" height="600"></iframe>`;
    } else {
      return url.toString();
    }
  }

  function setSelectedDates(from: Date, to: Date) {
    setDates({ from, to });
  }

  function isReadyToGenerate(): boolean {
    if (!dates) {
      return false;
    }
    if (!dates.from || !dates.to) {
      return false;
    }
    const fromTime = dates.from.getTime();
    if (fromTime > dates.to.getTime()) {
      return false;
    }
    if (fromTime < startOfDay(new Date()).getTime()) {
      return false;
    }
    return true;
  }

  async function copyClipboard() {
    if (link) {
      await navigator.clipboard.writeText(link);
    }
  }

  async function shareLink() {
    if (token && isShareAvailable()) {
      const shareLink = formatLink(token, false);
      try {
        await share({
          title: t('share.normalTitle'),
          url: shareLink,
        });
      } catch (error) {
        const ifUserAbortedShare = error instanceof DOMException && error.name == 'AbortError';
        if (ifUserAbortedShare) {
          return;
        }
        throw error;
      }
    }
  }

  return (
    <>
      <div className="share-link-datetime-range">
        <DateTimeRangeSelect
          setSelectedDates={setSelectedDates}
          startDate={dates.from}
          endDate={dates.to}
          minDateTime={startOfDay(new Date())}
        />
      </div>

      <Button
        variant="contained"
        disabled={!isReadyToGenerate()}
        onClick={createLink}
        className="share-link-generate"
        startIcon={<Refresh />}
      >
        {t('share.generate')}
      </Button>

      <FormControlLabel
        className="share-link-type"
        control={<Switch checked={embedded} onChange={toggleLink} />}
        label={t('share.embeddedTitle')}
      />
      <FormControl className="share-link-field" variant="outlined">
        <InputLabel shrink={link !== undefined} htmlFor="target-share-link">
          {t('share.normalTitle')}
        </InputLabel>
        <OutlinedInput
          ref={ref}
          id="target-share-link"
          type="text"
          label={t('share.normalTitle')}
          value={link}
          multiline
          rows={3}
          disabled={token == undefined}
          endAdornment={
            <InputAdornment position="end">
              {isShareAvailable() && (
                <IconButton
                  color="primary"
                  className="share-link-copy-link"
                  disabled={token == undefined}
                  onClick={shareLink}
                  edge="end"
                >
                  <Share />
                </IconButton>
              )}
              <IconButton color="primary" disabled={token == undefined} onClick={copyClipboard} edge="end">
                <ContentCopy />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </>
  );
}

function Header({ targetName }: { targetName: string }) {
  const { t } = useTranslation();

  return (
    <>
      <Stack>
        <Typography variant="h6">{t('share.normalTitle')}</Typography>
        <Typography sx={{ color: 'primary.main' }}>{targetName}</Typography>
      </Stack>
    </>
  );
}

export default function SharePopup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  async function closePopup() {
    await navigate(-1);
  }

  const targetIdStr = searchParams.get('targetId');
  const targetName = searchParams.get('targetName');
  if (!targetIdStr || !targetName) return null;
  const targetId = parseInt(targetIdStr, 10);
  if (isNaN(targetId) || targetId < 0) return null;

  return (
    <>
      <Popup
        popupTitle={undefined}
        header={<Header targetName={targetName} />}
        body={<Body targetId={targetId} />}
        footer={undefined}
        closePopup={closePopup}
        fullHeight={false}
        isFullScr={isPhoneOrHighScreen()}
        hideBackdrop={isPhoneOrHighScreen()}
        leftText={true}
      />
    </>
  );
}
